/**
 * Inventory Utilities
 *
 * Simple, direct functions for working with Etsy inventory_matrix data.
 * No transformations, no caching, just straightforward loops through the products array.
 *
 * See: docs/ETSY_DATA_STRUCTURE.md for data format
 */

/**
 * Extract base size name from Etsy size string
 * Examples:
 * - "MICRO height 0.6 inches" → "MICRO"
 * - "SMALL 1.4h" → "SMALL"
 * - "MED 2.6h" → "MED"
 *
 * @param {string} sizeString - Full size string from Etsy
 * @returns {string|null} - Base size name in uppercase
 */
export function extractBaseSizeName(sizeString) {
  if (!sizeString || typeof sizeString !== 'string') return null;

  // Get first word and uppercase it
  const baseName = sizeString.trim().split(/\s+/)[0].toUpperCase();

  // Normalize common variations
  const sizeMap = {
    MED: 'MEDIUM',
    LAR: 'LARGE',
    JUM: 'JUMBO',
    MAX: 'JUMBO',
  };

  return sizeMap[baseName] || baseName;
}

/**
 * Get price for a specific size+color combination
 *
 * Loops through inventory_matrix.products to find matching product.
 * Returns price directly from product.offerings[0].price
 *
 * @param {Object} inventory_matrix - Etsy inventory data with products array
 * @param {string} targetSize - Size to find (e.g., "MICRO" or "MICRO height 0.6")
 * @param {string} targetColor - Color to find (e.g., "Black", "Multi Color")
 * @returns {number|null} - Price in dollars (e.g., 5.99) or null if not found
 */
export function getPriceFromInventory(inventory_matrix, targetSize, targetColor) {
  if (!inventory_matrix?.products || !Array.isArray(inventory_matrix.products)) {
    return null;
  }

  if (!targetSize || !targetColor) {
    return null;
  }

  // Normalize target size to base name
  const targetBaseName = extractBaseSizeName(targetSize);
  if (!targetBaseName) return null;

  // Loop through all products to find matching size+color
  for (const product of inventory_matrix.products) {
    let productSize = null;
    let productColor = null;

    // Extract size and color from this product's property_values
    if (product.property_values && Array.isArray(product.property_values)) {
      for (const prop of product.property_values) {
        // Size can be in properties named: Size, Style, or Dimensions
        if (
          prop.property_name === 'Size' ||
          prop.property_name === 'Style' ||
          prop.property_name === 'Dimensions'
        ) {
          if (prop.values && prop.values[0]) {
            productSize = extractBaseSizeName(prop.values[0]);
          }
        }
        // Color is always in Primary color property
        else if (prop.property_name === 'Primary color') {
          if (prop.values && prop.values[0]) {
            productColor = prop.values[0];
          }
        }
      }
    }

    // Check if this product matches our target
    if (productSize === targetBaseName && productColor === targetColor) {
      // Found a match! Get the price
      if (product.offerings && product.offerings[0] && product.offerings[0].price) {
        const price = product.offerings[0].price;
        // Convert cents to dollars
        return price.amount / price.divisor;
      }
    }
  }

  // No matching product found
  return null;
}

/**
 * Get all available sizes from inventory
 *
 * @param {Object} inventory_matrix - Etsy inventory data
 * @returns {string[]} - Array of unique base size names (e.g., ["MICRO", "SMALL", "MED"])
 */
export function getAvailableSizes(inventory_matrix) {
  if (!inventory_matrix?.products || !Array.isArray(inventory_matrix.products)) {
    return [];
  }

  const sizes = new Set();

  for (const product of inventory_matrix.products) {
    if (product.property_values && Array.isArray(product.property_values)) {
      for (const prop of product.property_values) {
        if (
          prop.property_name === 'Size' ||
          prop.property_name === 'Style' ||
          prop.property_name === 'Dimensions'
        ) {
          if (prop.values && prop.values[0]) {
            const baseName = extractBaseSizeName(prop.values[0]);
            if (baseName) {
              sizes.add(baseName);
            }
          }
        }
      }
    }
  }

  return Array.from(sizes);
}

/**
 * Get available colors for a specific size
 *
 * @param {Object} inventory_matrix - Etsy inventory data
 * @param {string} targetSize - Size to filter by
 * @returns {string[]} - Array of unique color names for that size
 */
export function getAvailableColors(inventory_matrix, targetSize) {
  if (!inventory_matrix?.products || !Array.isArray(inventory_matrix.products)) {
    return [];
  }

  if (!targetSize) return [];

  const targetBaseName = extractBaseSizeName(targetSize);
  if (!targetBaseName) return [];

  const colors = new Set();

  for (const product of inventory_matrix.products) {
    let productSize = null;
    let productColor = null;

    if (product.property_values && Array.isArray(product.property_values)) {
      for (const prop of product.property_values) {
        if (
          prop.property_name === 'Size' ||
          prop.property_name === 'Style' ||
          prop.property_name === 'Dimensions'
        ) {
          if (prop.values && prop.values[0]) {
            productSize = extractBaseSizeName(prop.values[0]);
          }
        } else if (prop.property_name === 'Primary color') {
          if (prop.values && prop.values[0]) {
            productColor = prop.values[0];
          }
        }
      }
    }

    // If this product matches the target size, add its color
    if (productSize === targetBaseName && productColor) {
      colors.add(productColor);
    }
  }

  return Array.from(colors);
}

/**
 * Get inventory quantity for a specific size+color
 *
 * @param {Object} inventory_matrix - Etsy inventory data
 * @param {string} targetSize - Size to find
 * @param {string} targetColor - Color to find
 * @returns {number} - Quantity available (0 if not found)
 */
export function getInventoryQuantity(inventory_matrix, targetSize, targetColor) {
  if (!inventory_matrix?.products || !Array.isArray(inventory_matrix.products)) {
    return 0;
  }

  const targetBaseName = extractBaseSizeName(targetSize);
  if (!targetBaseName) return 0;

  for (const product of inventory_matrix.products) {
    let productSize = null;
    let productColor = null;

    if (product.property_values && Array.isArray(product.property_values)) {
      for (const prop of product.property_values) {
        if (
          prop.property_name === 'Size' ||
          prop.property_name === 'Style' ||
          prop.property_name === 'Dimensions'
        ) {
          if (prop.values && prop.values[0]) {
            productSize = extractBaseSizeName(prop.values[0]);
          }
        } else if (prop.property_name === 'Primary color') {
          if (prop.values && prop.values[0]) {
            productColor = prop.values[0];
          }
        }
      }
    }

    if (productSize === targetBaseName && productColor === targetColor) {
      if (product.offerings && product.offerings[0]) {
        return product.offerings[0].quantity || 0;
      }
    }
  }

  return 0;
}
